import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useGeneralExams, useLeaderboard, useInviteFriend } from '../lib/api';
import { VerifiedBadge } from '../components/VerifiedBadge';
import { PageTransition, FadeIn, Stagger, Pressable, CountUp, Confetti } from '../lib/motion';
import { haptic } from '../lib/haptics';

export function ExamLeaderboardPage() {
  const { examId } = useParams();
  const { data: examsData } = useGeneralExams();
  const { data: lb } = useLeaderboard('exam', examId ?? null);

  const exam = examsData?.exams?.find(e => e.id === examId);
  const entries = lb?.entries ?? [];
  const PAGE_SIZE = 20;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const visible = entries.slice(0, visibleCount);
  const hasMore = visibleCount < entries.length;

  // Celebrate once if the viewer holds a podium spot. Decorative only.
  const podium = entries.slice(0, 3);
  const userOnPodium = podium.some(e => e.isCurrentUser);
  const celebrated = useRef(false);
  const [celebrate, setCelebrate] = useState(false);
  useEffect(() => {
    if (userOnPodium && !celebrated.current) {
      celebrated.current = true;
      setCelebrate(true);
      haptic.success();
    }
  }, [userOnPodium]);

  return (
    <PageTransition className="relative max-w-lg mx-auto px-4 pb-24">
      {celebrate && <Confetti count={28} />}

      {/* Header */}
      <div className="flex items-center gap-1 pt-1 mb-6">
        <Link
          to={`/exam/${examId}`}
          aria-label="Back to exam dashboard"
          className="w-11 h-11 -ml-2 flex items-center justify-center rounded-full text-squishy-primary hover:bg-teal-50 transition-colors shrink-0"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd" />
          </svg>
        </Link>
        <div>
          <h1 className="font-display text-xl font-semibold text-squishy-dark tracking-tight leading-tight">Leaderboard</h1>
          <p className="text-[11px] text-slate-500">{exam?.code ?? ''} · last 30 days</p>
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="text-center py-16">
          <div className="mx-auto w-14 h-14 rounded-3xl bg-amber-50 flex items-center justify-center mb-4" aria-hidden>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.7} stroke="currentColor" className="w-6 h-6 text-squishy-gold">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0" />
            </svg>
          </div>
          <p className="text-sm font-medium text-slate-500">No activity yet.</p>
          <p className="text-xs text-slate-400 mt-1">Answer a few questions and the top spot is yours.</p>
        </div>
      ) : (
        <>
          {/* Podium */}
          <FadeIn className="mb-6">
            <div className="flex items-end justify-center gap-3">
              {[podium[1], podium[0], podium[2]].map((e, i) =>
                e ? <PodiumSpot key={e.userId} entry={e} place={i === 1 ? 1 : i === 0 ? 2 : 3} /> : null,
              )}
            </div>
          </FadeIn>

          {/* Ranks 4 and beyond */}
          {visible.length > 3 && (
            <div className="bg-white border border-slate-100 rounded-3xl shadow-card overflow-hidden">
              <Stagger>
                <ol className="divide-y divide-slate-100">
                  {visible.slice(3).map(e => (
                    <Stagger.Item key={e.userId}>
                      <LeaderboardRow entry={e} />
                    </Stagger.Item>
                  ))}
                </ol>
              </Stagger>
            </div>
          )}

          {hasMore && (
            <Pressable
              onClick={() => setVisibleCount(prev => prev + PAGE_SIZE)}
              className="w-full mt-4 min-h-[48px] py-3 bg-slate-100 hover:bg-slate-200 rounded-2xl text-sm font-semibold text-slate-600 transition-colors"
            >
              Show More ({entries.length - visibleCount} remaining)
            </Pressable>
          )}

          {lb?.currentUserRank && (
            <FadeIn delay={0.2} className="mt-4">
              <div className="flex items-center justify-center gap-2 rounded-2xl bg-teal-50 border border-teal-200 py-3 px-4">
                <span className="text-xs font-semibold text-squishy-primary">
                  Your rank: #{lb.currentUserRank} of {lb.totalUsers}
                </span>
              </div>
            </FadeIn>
          )}
        </>
      )}
    </PageTransition>
  );
}

// ── Podium spot (top 3) ─────────────────────────────────────────────────────

const PODIUM = {
  1: { pedestal: 'h-24', ring: 'ring-4 ring-squishy-gold', avatar: 'w-16 h-16 text-base', num: 'text-amber-500', chip: 'bg-amber-50 text-amber-600' },
  2: { pedestal: 'h-16', ring: 'ring-2 ring-slate-300', avatar: 'w-14 h-14 text-sm', num: 'text-slate-400', chip: 'bg-slate-100 text-slate-500' },
  3: { pedestal: 'h-12', ring: 'ring-2 ring-orange-300', avatar: 'w-14 h-14 text-sm', num: 'text-orange-400', chip: 'bg-orange-50 text-orange-600' },
} as const;

function PodiumSpot({ entry: e, place }: { entry: any; place: 1 | 2 | 3 }) {
  const invite = useInviteFriend();
  const [showAdd, setShowAdd] = useState(false);
  const p = PODIUM[place];

  return (
    <div className="flex flex-col items-center w-24 min-w-0">
      <Pressable
        onClick={() => { if (!e.isCurrentUser) setShowAdd(!showAdd); }}
        aria-label={e.isCurrentUser ? `${e.displayName}, rank ${e.rank}` : `${e.displayName}, rank ${e.rank}. Tap to add friend.`}
        className="flex flex-col items-center gap-1.5 min-w-0 w-full pt-1"
      >
        <div className={`${p.avatar} ${p.ring} rounded-full bg-slate-200 overflow-hidden flex items-center justify-center font-semibold text-slate-600 shrink-0`}>
          {e.avatarUrl ? <img src={e.avatarUrl} alt="" className="w-full h-full object-cover" /> : e.displayName[0]}
        </div>
        <span className="max-w-full text-xs font-semibold text-squishy-dark truncate inline-flex items-center gap-1">
          <span className="truncate">{e.displayName}</span>
          {e.verifiedStudent && <VerifiedBadge />}
        </span>
        <span className={`text-[10px] font-bold tabular-nums px-2 py-0.5 rounded-full ${p.chip}`}>
          <CountUp value={e.xp} /> XP
        </span>
      </Pressable>
      {showAdd && !e.isCurrentUser && (
        <Pressable
          haptic="medium"
          onClick={async (ev) => {
            ev.stopPropagation();
            await invite.mutateAsync({ userId: e.userId });
            setShowAdd(false);
          }}
          disabled={invite.isPending}
          className="mt-1 bg-squishy-primary text-white text-[10px] font-semibold px-3 py-1.5 rounded-full disabled:opacity-50 shadow-soft"
        >
          {invite.isSuccess ? 'Sent!' : invite.isPending ? '...' : '+ Friend'}
        </Pressable>
      )}
      <div className={`mt-2 w-full ${p.pedestal} rounded-t-2xl bg-slate-100 flex items-start justify-center pt-1.5 ${e.isCurrentUser ? 'bg-teal-50' : ''}`}>
        <span className={`font-display text-xl font-bold ${p.num}`}>{e.rank}</span>
      </div>
    </div>
  );
}

// ── List row (rank 4+) ──────────────────────────────────────────────────────

function LeaderboardRow({ entry: e }: { entry: any }) {
  const invite = useInviteFriend();
  const [showAdd, setShowAdd] = useState(false);

  return (
    <li className={`flex items-center gap-3 px-4 py-3 ${e.isCurrentUser ? 'bg-teal-50' : ''}`}>
      <span className="w-7 text-right text-xs font-bold tabular-nums text-slate-400 shrink-0">{e.rank}</span>
      <div className="w-9 h-9 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center text-[11px] font-semibold text-slate-600 shrink-0">
        {e.avatarUrl ? <img src={e.avatarUrl} alt="" className="w-full h-full object-cover" /> : e.displayName[0]}
      </div>
      <button
        className="flex-1 min-h-[44px] text-left text-sm font-medium text-squishy-dark hover:text-squishy-primary transition-colors inline-flex items-center gap-1 min-w-0"
        onClick={() => { if (!e.isCurrentUser) setShowAdd(!showAdd); }}
      >
        <span className="truncate">{e.displayName}</span>
        {e.verifiedStudent && <VerifiedBadge />}
      </button>
      {showAdd && !e.isCurrentUser && (
        <Pressable
          haptic="medium"
          onClick={async (ev) => {
            ev.stopPropagation();
            await invite.mutateAsync({ userId: e.userId });
            setShowAdd(false);
          }}
          disabled={invite.isPending}
          className="shrink-0 bg-squishy-primary text-white text-[10px] font-semibold px-3 py-1.5 rounded-full disabled:opacity-50 shadow-soft"
        >
          {invite.isSuccess ? 'Sent!' : invite.isPending ? '...' : '+ Friend'}
        </Pressable>
      )}
      <span className="text-xs font-semibold text-squishy-dark tabular-nums shrink-0">
        <CountUp value={e.xp} /> <span className="text-slate-400 font-medium">XP</span>
      </span>
    </li>
  );
}
